import { createProjector } from 'maquette'
import { cloneDeep, find } from 'lodash-es'

import { v4 as uuid } from 'uuid'

import templateHelpers from './template-helpers'
import render from './scene-editor.pug'
import CONDITIONS from './trigger-editor/conditions'
import TriggerEditor from './trigger-editor'
import EditorBase from './editor-base'

export default class SceneEditor extends EditorBase
  @CONDITIONS: CONDITIONS

  @DEFAULT_DISPLAY = { type: "webview" }
  @INITIAL_TRIGGER_TYPE = "_initial"

  @AUDIO_TRACK_INT_FIELDS = ["assetId", "instanceId", "repeat"]
  @AUDIO_TRACK_FLOAT_FIELDS = ["currentTime", "fadeInSeconds", "fadeInSeconds", "repeat"]

  @property "assetsManager", get: -> @playbookEditor.assetsManager

  @property "triggerEditors", get: ->
    @scene.triggers.map (trigger)=> @triggerEditorFor(trigger)

  @property "allTriggerEditors", get: ->
    [@initialTriggerEditor].concat(@triggerEditors)

  @property "currentFullActionEditor",
    get: -> @_currentFullActionEditor
    set: (actionEditor) ->
      return if actionEditor == @_currentFullActionEditor

      previous = @_currentFullActionEditor
      @_currentFullActionEditor = actionEditor
      @projector.scheduleRender()

      if actionEditor
        # Push one entry so browser back exits and forward re-enters the view.
        @updateHashParams(
          action: actionEditor.constructor.type
          ref: unless actionEditor.editor.initial then actionEditor.editor.trigger._ref
        )
      else if previous
        @updateHashParams({action: null, ref: null}, replace: true)

  projector: createProjector()

  constructor: ({scene, playbookEditor})->
    super()

    document.addEventListener "keydown", @onKeydown

    @scene = scene
    @playbookEditor = playbookEditor
    @scene.initial ||= {}
    @scene.initial.display ||= Object.assign({}, @constructor.DEFAULT_DISPLAY)
    @scene.triggers ||= []

    @triggerEditorByTrigger = new Map()
    @initialTrigger =
      type: @constructor.INITIAL_TRIGGER_TYPE
      actions: @scene.initial
    @initialTriggerEditor = new TriggerEditor(
      trigger: @initialTrigger
      sceneEditor: this
      initial: true
    )

    @playbookEditor.currentSceneEditor = this

    document.body.classList.add "is-editing-scene"
    @projector.append(@playbookEditor.mainElement || document.querySelector("main"), @_render)

  deleteTrigger: (trigger)=>
    return if trigger == @initialTrigger

    conditionClass = @constructor.CONDITIONS[trigger.type]
    label = trigger._name || conditionClass?.label || trigger.type
    return unless confirm(@t('.confirm_delete_condition', label: label))

    return if (index = @scene.triggers.indexOf(trigger)) == -1

    @scene.triggers.splice(index, 1)
    @triggerEditorByTrigger.delete(trigger)
    @projector.scheduleRender()

  onKeydown: (e)=>
    return unless e.key == "Escape"

    if @currentFullActionEditor
      @dismissFullActionEditor()
    else
      @dismiss()

  addTrigger: (conditionClass, e)=>
    newTrigger = conditionClass.scaffold()
    @scene.triggers.push(newTrigger)

    @focusTrigger(newTrigger)

    e.target.blur() if e

  onSceneNameInput: (e)=>
    @scene._name = e.target.value

  triggerEditorFor: (trigger)->
    return @initialTriggerEditor if trigger == @initialTrigger

    unless (triggerEditor = @triggerEditorByTrigger.get(trigger))
      triggerEditor = new TriggerEditor(
        trigger: trigger
        sceneEditor: this
      )
      @triggerEditorByTrigger.set(trigger, triggerEditor)

    triggerEditor

  focusTrigger: (trigger)=>
    triggerEditor = @triggerEditorFor(trigger)
    @projector.scheduleRender()

    requestAnimationFrame =>
      triggerEditor.element?.scrollIntoView(
        behavior: "smooth"
        block: "nearest"
        inline: "center"
      )

  bindBoard: (@boardElement)=>

  onBoardWheel: (e)=>
    return unless @boardElement
    return if @_shouldKeepNativeWheel(e)

    deltaY = e.deltaY
    switch e.deltaMode
      when 1 then deltaY *= 16
      when 2 then deltaY *= @boardElement.clientWidth

    return unless deltaY

    @boardElement.scrollLeft += deltaY
    e.preventDefault()

  _shouldKeepNativeWheel: (e)->
    return true if Math.abs(e.deltaX) > Math.abs(e.deltaY)
    return true if e.target.closest?("input, textarea, select, option")

    element = e.target
    while element && element != @boardElement
      if element.scrollHeight > element.clientHeight
        style = window.getComputedStyle(element)
        if style.overflowY in ["auto", "scroll"]
          canScrollUp = element.scrollTop > 0
          canScrollDown = element.scrollTop + element.clientHeight < element.scrollHeight - 1

          return true if e.deltaY < 0 && canScrollUp
          return true if e.deltaY > 0 && canScrollDown

      element = element.parentElement

    false

  save: ->
    for triggerEditor in @allTriggerEditors
      await triggerEditor.save()

    await @assetsManager.upload()

    @playbookEditor.save()

  showFullActionEditor: (actionEditor) =>
    await @dismissFullActionEditor() if @currentFullActionEditor

    @currentFullActionEditor = actionEditor

  # Opens the full-screen action a deep link points at (#…&action=&ref=).
  # `ref` is the trigger _ref, absent means the initial trigger
  onLocationHashChange: =>
    params = @hashParams()
    actionType = params.get("action")

    unless actionType
      @dismissFullActionEditor()
      return

    if (ref = params.get("ref"))
      return unless (trigger = find(@scene.triggers, _ref: ref))
      triggerEditor = @triggerEditorFor(trigger)
    else
      triggerEditor = @initialTriggerEditor

    return unless triggerEditor.actions[actionType]
    return unless (actionEditor = triggerEditor.actionEditorFor(actionType))
    return if actionEditor == @currentFullActionEditor

    @showFullActionEditor(actionEditor)

  dismissFullActionEditor: =>
    return unless (actionEditor = @currentFullActionEditor)

    await actionEditor.save()
    await @save()

    actionEditor.form = null
    @currentFullActionEditor = null

  dismiss: =>
    return if @dismissing

    @dismissing = true

    @currentFullActionEditor.save() if @currentFullActionEditor

    document.removeEventListener "keydown", @onKeydown
    @teardownLocationHash()
    @playbookEditor.currentSceneEditor = null if @playbookEditor.currentSceneEditor == this

    await @save()

    projection = @projector.detach(@_render)
    projection.domNode.remove()
    document.body.classList.remove "is-editing-scene"

  delete: =>
    if @scene in @playbookEditor.usedScenes
      return unless confirm(@t('.confirm_delete_used'))
    else
      return unless confirm(@t('.confirm_delete'))

    @playbookEditor.scenes.splice(@playbookEditor.scenes.indexOf(@scene), 1)
    @dismiss()

  promptCopy: =>
    return unless copies = prompt(@t('.prompt_copy'), "1")

    await @save()

    copies = parseInt(copies, 10)

    for _i in [1..copies]
      copy = cloneDeep(@scene)
      copy.id = uuid()

      for trigger in copy.triggers
        trigger._ref = uuid()

      @playbookEditor.scenes.push(copy)

    @playbookEditor.redraw()
    @playbookEditor.save()

  _render: =>
    render.call(this, templateHelpers)[0]
